import { useToast } from '@common/hooks';
import { Base64 } from 'js-base64';
import { useEffect, useRef } from 'react';

import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { ensureRenderableImage } from '@/utils/heif';

import { useCheckWelloApp } from './useCheckWelloApp';

interface UseFileUploadProps {
  accept?: string;
  multiple?: boolean;
}

export const useFileUpload = ({
  accept,
  multiple = false,
}: UseFileUploadProps) => {
  const { request, response } = useHandleApp<{
    image: string;
  }>({
    type: MESSAGE_KEYWORD.IMAGE,
  });

  const { isWelloApp } = useCheckWelloApp();
  const { toast } = useToast();

  const image64 = response?.data?.image;

  const resolveRef = useRef<((value: File | undefined) => void) | null>(null);
  const resolveFilesRef = useRef<((value: File[]) => void) | null>(null);

  const file = useRef<File | undefined>(undefined);
  const files = useRef<File[]>([]);

  useEffect(() => {
    if (!image64) return;

    let isCancelled = false;

    const buildAndResolve = async () => {
      const mime = 'image/png';

      // Base64 문자열을 바이트 코드 배열로 디코딩
      const byteCharacters = Base64.atob(image64);
      const byteArrays: BlobPart[] = [];

      // 512바이트씩 처리
      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);

        const byteNumbers = new Array(slice.length);
        for (let index = 0; index < slice.length; index++) {
          byteNumbers[index] = slice.codePointAt(index) ?? 0;
        }

        byteArrays.push(new Uint8Array(byteNumbers));
      }

      const blob = new Blob(byteArrays, { type: mime });

      // 네이티브 앱은 실제 포맷과 무관하게 항상 image/png로 보내므로 매직 바이트로 HEIF를 판별해 변환한다.
      let resolvedFile: File | undefined;
      try {
        resolvedFile = await ensureRenderableImage(
          new File([blob], 'image.png', { type: mime }),
        );
      } catch {
        if (!isCancelled) {
          toast({
            message: '이미지 변환에 실패했어요. 다른 사진을 선택해 주세요.',
            type: 'fail',
          });
        }
      }

      if (isCancelled) return;

      file.current = resolvedFile;
      resolveRef.current?.(resolvedFile);
      resolveRef.current = null;
      files.current = resolvedFile ? [resolvedFile] : [];
      resolveFilesRef.current?.(files.current);
      resolveFilesRef.current = null;
    };

    void buildAndResolve();

    return () => {
      isCancelled = true;
    };
  }, [image64, toast]);

  const openFilePicker = (pickMultiple: boolean) => {
    //! 혹시 남아있을지 모를 대기중인 응답을 초기화
    resolveRef.current?.(undefined);
    resolveRef.current = null;
    resolveFilesRef.current?.([]);
    resolveFilesRef.current = null;
    file.current = undefined;
    files.current = [];

    if (isWelloApp) {
      request();
    } else {
      const fileInput = document.createElement('input');

      fileInput.type = 'file';
      if (accept) fileInput.accept = accept;
      if (pickMultiple) fileInput.multiple = true;

      fileInput.style.appearance = 'hide';
      fileInput.addEventListener('change', async (event) => {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
          const selectedFiles = Array.from(input.files);
          const validFiles: File[] = [];

          for (const selectedFile of selectedFiles) {
            try {
              validFiles.push(await ensureRenderableImage(selectedFile));
            } catch {
              continue;
            }
          }

          if (validFiles.length < selectedFiles.length) {
            toast({
              message: '일부 이미지를 변환하지 못했어요.',
              type: 'fail',
            });
          }

          file.current = validFiles[0];
          files.current = validFiles;
        }
        resolveRef.current?.(file.current);
        resolveRef.current = null;
        resolveFilesRef.current?.(files.current);
        resolveFilesRef.current = null;
      });

      fileInput.addEventListener('cancel', () => {
        resolveRef.current?.(undefined);
        resolveRef.current = null;
        resolveFilesRef.current?.([]);
        resolveFilesRef.current = null;
      });

      fileInput.click();
    }
  };

  return {
    getFile: async () => {
      openFilePicker(false);

      return new Promise<File | undefined>((resolve) => {
        resolveRef.current = resolve;
      });
    },
    getFiles: async () => {
      openFilePicker(multiple);

      return new Promise<File[]>((resolve) => {
        resolveFilesRef.current = resolve;
      });
    },
  };
};
